import { Model } from './Model';
import { defineApi, type IApiDefinition, type IBaseModelApi, type TApi, type ISetup as IApiSetup } from './defineApi';
import { TOverride } from './utils/types';
import { defineRepository, type IRepositoryDefinition, type TRepository, type ISetup as IRepositorySetup } from './defineRepository';
import { definePagination, IPaginationInstance } from './definePagination';

type TAsyncFunction = (...args: any[]) => Promise<any>;

type TActionsContext<M extends Model, ApiEndpoints, CustomRepositoryMethods, CustomActions> =
  & CustomActions
  & ThisType<CustomActions & ISetupContext<M, ApiEndpoints, CustomRepositoryMethods>>
  ;

type TController<M extends Model, ApiEndpoints, RepositoryMethods, CustomActions> =
  & IBaseController<M, ApiEndpoints, RepositoryMethods>
  & CustomActions
  & TOverride<IBaseModelApi<M>, CustomActions>
  & TOverride<ApiEndpoints, CustomActions>
  ;

type TReactionsTree<ApiEndpoints> = {
  [K in keyof ApiEndpoints]-?: TReactionFunction;
};

type TReactionFunction = (response: any, ...args: any[]) => void;

interface IBaseController<M extends Model, ApiEndpoints, RepositoryMethods> {
  api: TApi<M, ApiEndpoints>;
  repository: TRepository<M, RepositoryMethods>;
  pagination: IPaginationInstance;
}

interface ISetupContext<M extends Model, ApiEndpoints, CustomRepositoryMethods> {
  api: TApi<M, ApiEndpoints>;
  repository: TRepository<M, CustomRepositoryMethods>;
}

interface ISetup<M extends Model, ApiEndpoints, RepositoryMethods, CustomActions, Reactions> {
  actions?: TActionsContext<M, ApiEndpoints, RepositoryMethods, CustomActions>;
  reactions?: Reactions;
  api?: IApiSetup<M, ApiEndpoints>;
  repository?: IRepositorySetup<M, RepositoryMethods>;
}

interface IControllerDefinition<M extends Model, ApiEndpoints, RepositoryMethods, CustomActions> {
  (): TController<M, ApiEndpoints, RepositoryMethods, CustomActions>;
}

function _defineController<
  M extends Model,
  Reactions extends TReactionsTree<CustomApiEndpoints>,
  CustomApiEndpoints = {},
  CustomRepositoryMethods = {},
  CustomActions = {},
>(
  ModelClass: new () => M,
  apiDefinition?: IApiDefinition<M, CustomApiEndpoints>,
  repositoryDefinition?: IRepositoryDefinition<M, CustomRepositoryMethods>,
  setup?: ISetup<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions, Reactions>,
): IControllerDefinition<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions> {
  const _useApi = apiDefinition
    ? apiDefinition
    : defineApi<M, CustomApiEndpoints>(ModelClass, setup?.api);
  const _useRepository = repositoryDefinition
    ? repositoryDefinition
    : defineRepository<M, CustomRepositoryMethods>(ModelClass, setup?.repository);

  const api = _useApi();
  const baseActions = {} as typeof api;
  const setupActions = setup?.actions ?? {} as CustomActions;
  const setupReactions = setup?.reactions ?? {} as Reactions;
  const usePagination = definePagination(ModelClass.name);

  function getResponseData(response: any): any {
    return response._apiResponse
      ? response.getData()
      : response;
  }

  return function useController() {
    const repository = _useRepository();
    const pagination = usePagination();

    const responseHandlers = {
      fetchAll(response: any): void {
        repository.set(getResponseData(response));
      },
      fetchOne(response: any): void {
        repository.add(getResponseData(response));
      },
      fetchMany(response: any): void {
        repository.add(getResponseData(response));
      },
      createOne(response: any): void {
        repository.add(getResponseData(response));
      },
      updateOne(response: any): void {
        repository.update(getResponseData(response));
      },
      updateMany(models: any): void {
        models.forEach((model: any) => repository.update(model));
      },
      deleteOne(_: any, ...args: any[]): void {
        repository.remove(args[0]);
      },
      deleteMany(_: any, ...args: any[]): void {
        repository.remove(args);
      },
    } as Record<keyof typeof api, (response: any, ...args: any[]) => void>;

    Object.keys(api).forEach((key: string) => {
      const endpointKey = key as keyof typeof api;

      baseActions[endpointKey] = async function (...args: any[]): Promise<any> {
        const response = await (api[endpointKey] as TAsyncFunction)(...args);
        if (response._apiResponse && response.isPaginated()) {
          pagination.set(response.getPagination() ?? null);
        }
        if (endpointKey in responseHandlers) {
          responseHandlers[endpointKey](response, ...args);
        } else if (endpointKey in setupReactions) {
          (setupReactions[endpointKey as keyof Reactions] as TReactionFunction)(response, ...args);
        }
        return response;
      } as any;
    });

    return {
      api,
      repository,
      pagination,
      ...baseActions,
      ...setupActions,
    };
  };
}

function defineController<
  M extends Model,
  Reactions extends TReactionsTree<CustomApiEndpoints>,
  CustomApiEndpoints = {},
  CustomRepositoryMethods = {},
  CustomActions = {},
>(
  ModelClass: new () => M,
  setup?: ISetup<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions, Reactions>,
): IControllerDefinition<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions>;
function defineController<
  M extends Model,
  Reactions extends TReactionsTree<CustomApiEndpoints>,
  CustomApiEndpoints = {},
  CustomRepositoryMethods = {},
  CustomActions = {},
>(
  ModelClass: new () => M,
  apiDefinition: IApiDefinition<M, CustomApiEndpoints>,
  setup?: ISetup<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions, Reactions>,
): IControllerDefinition<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions>;
function defineController<
  M extends Model,
  Reactions extends TReactionsTree<CustomApiEndpoints>,
  CustomApiEndpoints = {},
  CustomRepositoryMethods = {},
  CustomActions = {},
>(
  ModelClass: new () => M,
  repositoryDefinition: IRepositoryDefinition<M, CustomRepositoryMethods>,
  setup?: ISetup<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions, Reactions>,
): IControllerDefinition<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions>;
function defineController<
  M extends Model,
  Reactions extends TReactionsTree<CustomApiEndpoints>,
  CustomApiEndpoints = {},
  CustomRepositoryMethods = {},
  CustomActions = {},
>(
  ModelClass: new () => M,
  apiDefinition: IApiDefinition<M, CustomApiEndpoints>,
  repositoryDefinition: IRepositoryDefinition<M, CustomRepositoryMethods>,
  setup?: ISetup<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions, Reactions>,
): IControllerDefinition<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions>;
function defineController<
  M extends Model,
  Reactions extends TReactionsTree<CustomApiEndpoints>,
  CustomApiEndpoints = {},
  CustomRepositoryMethods = {},
  CustomActions = {},
>(
  ModelClass: new () => M,
  apiDefinition?: IApiDefinition<M, CustomApiEndpoints> | ISetup<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions, Reactions>,
  repositoryDefinition?: IRepositoryDefinition<M, CustomRepositoryMethods> | ISetup<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions, Reactions>,
  setup?: ISetup<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions, Reactions>,
): IControllerDefinition<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions> {
  let _apiDefinition: IApiDefinition<M, CustomApiEndpoints> | undefined;
  let _repositoryDefinition: IRepositoryDefinition<M, CustomRepositoryMethods> | undefined;

  if (arguments.length === 4) {
    _apiDefinition = apiDefinition as IApiDefinition<M, CustomApiEndpoints>;
    _repositoryDefinition = repositoryDefinition as IRepositoryDefinition<M, CustomRepositoryMethods>;
  } else if (arguments.length === 3) {
    const functionName = arguments[1].name.toLowerCase();
    const composable = functionName.includes('api')
      ? 'api'
      : 'repository';
    _apiDefinition = composable === 'api'
      ? arguments[1]
      : undefined;
    _repositoryDefinition = composable === 'repository'
      ? arguments[1]
      : undefined;
    setup = arguments[2] as ISetup<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions, Reactions>;
  } else if (arguments.length === 2) {
    if (typeof arguments[1] === 'function') {
      const functionName = arguments[1].name.toLowerCase();
      const composable = functionName.includes('api')
        ? 'api'
        : 'repository';
      _apiDefinition = composable === 'api'
        ? arguments[1]
        : undefined;
      _repositoryDefinition = composable === 'repository'
        ? arguments[1]
        : undefined;
    } else {
      setup = arguments[1] as ISetup<M, CustomApiEndpoints, CustomRepositoryMethods, CustomActions, Reactions>;
    }
  }

  return _defineController(ModelClass, _apiDefinition, _repositoryDefinition, setup);
}

export {
  defineController,
};
